/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { type Workspace, WorkspaceSwitcher } from "@iam/design-system/agent";
import { StatusBadge } from "@iam/design-system/chat";
import { useI18n, useTranslation } from "@iam/design-system/i18n";
import { AppShell, Header, SidebarNav } from "@iam/design-system/layout";
import { Button } from "@iam/design-system/primitives";
import { Activity, Bot, Globe, Wrench } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SessionRunner } from "./components/SessionRunner";
import { SessionView } from "./components/SessionView";
import { ToolCatalog } from "./components/ToolCatalog";
import { GatewayClient } from "./lib/gatewayClient";
import { useSessionStore } from "./lib/sessionStore";
import type { GatewayIdentity } from "./lib/types";
import type { CoreOIDCSession } from "./auth/session";

/**
 * The IAM.Core Web Console (CORE-011).
 *
 * The user picks a workspace, runs an agent session against iam-core-gateway,
 * and watches the tool-call timeline, run metrics and approvals — all driven
 * by the gateway's real HTTP surface (/v1/tools, /v1/agent/sessions, /mcp).
 */

// The console's bootstrap identity. In production this is derived from a
// verified OIDC session (CORE-050); here it is a fixed dev tenant.
const IDENTITY: GatewayIdentity = {
  tenantId: "acme-prod",
  principal: "console-user",
  scopes: ["core:read", "docs:read"],
  groups: ["legal"],
};

// The console-side workspace catalog. Workspaces are a console concept layered
// over the gateway's tenant; the gateway is workspace-aware via workspace_id.
const WORKSPACES: ReadonlyArray<Workspace> = [
  { id: "ws-legal", name: "Legal & Contracts", slug: "legal" },
  { id: "ws-ops", name: "Operations", slug: "ops" },
  { id: "ws-research", name: "Research", slug: "research" },
];

const BUDGET_USD = 1;

type NavTab = "session" | "tools" | "metrics";

export type AppProps = {
  /** Injectable client — production builds the default; tests pass a fake. */
  client?: GatewayClient;
  session?: CoreOIDCSession;
};

/** Root IAM.Core console application: tools, session runner, and live session view. */
export function App({ client: injectedClient, session }: AppProps) {
  const t = useTranslation();
  const { locale, setLocale } = useI18n();

  const identity = useMemo<GatewayIdentity>(() => session ? {
    tenantId: "",
    principal: session.subject,
    scopes: [],
  } : IDENTITY, [session]);

  const client = useMemo(
    () => injectedClient ?? new GatewayClient({
      baseUrl: "", identity, cookieAuth: Boolean(session),
    }),
    [identity, injectedClient, session],
  );

  const store = useSessionStore({
    client,
    workspaces: WORKSPACES,
    principal: identity.principal,
    defaultBudget: { max_iterations: 12, max_cost_usd: BUDGET_USD },
  });

  const [tab, setTab] = useState<NavTab>("session");
  const [online, setOnline] = useState<boolean | null>(null);

  const loadTools = store.loadTools;

  // Probe gateway health on mount / when the client changes, and load the
  // tenant's tool catalog. loadTools is a stable useCallback from the store.
  useEffect(() => {
    let cancelled = false;
    client
      .health()
      .then(() => !cancelled && setOnline(true))
      .catch(() => !cancelled && setOnline(false));
    void loadTools();
    return () => {
      cancelled = true;
    };
  }, [client, loadTools]);

  const handleApprove = useCallback(
    (approvalId: string, comment?: string) => {
      void store.decideApproval(approvalId, "approved", comment);
    },
    [store],
  );

  const handleDeny = useCallback(
    (approvalId: string, reason: string) => {
      void store.decideApproval(approvalId, "denied", reason);
    },
    [store],
  );

  const navItems = [
    { id: "session", label: t("nav.session"), icon: Bot },
    { id: "tools", label: t("nav.tools"), icon: Wrench, badge: store.tools.length || undefined },
    { id: "metrics", label: t("nav.metrics"), icon: Activity },
  ];

  return (
    <AppShell
      header={
        <Header
          brand={
            <span className="console-brand">
              <Bot size={20} aria-hidden="true" />
              <span className="console-brand__name">{t("app.title")}</span>
            </span>
          }
          center={
            <WorkspaceSwitcher
              workspaces={WORKSPACES}
              currentWorkspaceId={store.workspaceId}
              onSelect={store.selectWorkspace}
              data-testid="console-workspace-switcher"
            />
          }
          actions={
            <div className="console-header-actions">
              <span data-testid="console-gateway-status">
                <StatusBadge
                  kind={online === false ? "danger" : online ? "success" : "idle"}
                  label={online === false ? t("status.down") : t("status.ok")}
                />
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Globe />}
                onClick={() => setLocale(locale === "ru" ? "en" : "ru")}
                data-testid="console-lang-toggle"
              >
                {locale.toUpperCase()}
              </Button>
            </div>
          }
        />
      }
      sidebar={
        <SidebarNav
          items={navItems}
          activeId={tab}
          onSelect={(id) => setTab(id as NavTab)}
          footer={<span className="console-version">{t("app.subtitle")}</span>}
        />
      }
    >
      <div className="console-main" data-testid="console-main">
        {store.error && (
          <div className="console-error" role="alert" data-testid="console-error">
            <strong>{t("error.gateway")}:</strong> {store.error}
            <button
              type="button"
              className="console-error__dismiss"
              onClick={store.clearError}
              aria-label="dismiss"
            >
              ×
            </button>
          </div>
        )}

        {tab === "session" && (
          <>
            <SessionRunner
              running={store.running}
              onRun={(goal, mode) => void store.runSession(goal, mode)}
              onCancel={() => void store.cancelSession()}
              data-testid="console-runner"
            />
            {store.session ? (
              <SessionView
                session={store.session}
                budgetUsd={BUDGET_USD}
                onApprove={handleApprove}
                onDeny={handleDeny}
                onCancel={() => void store.cancelSession()}
                data-testid="console-session"
              />
            ) : (
              <p className="console-empty" data-testid="console-session-empty">
                {t("session.empty")}
              </p>
            )}
          </>
        )}

        {tab === "tools" && <ToolCatalog tools={store.tools} data-testid="console-tools" />}

        {tab === "metrics" && (
          <div className="console-metrics-tab" data-testid="console-metrics-tab">
            {store.session ? (
              <SessionView
                session={store.session}
                budgetUsd={BUDGET_USD}
                onApprove={handleApprove}
                onDeny={handleDeny}
              />
            ) : (
              <p className="console-empty">{t("session.empty")}</p>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
